/**
 * Input Component
 *
 * A text input component with label, error state, and disabled state support.
 * Provides consistent styling and accessibility features.
 *
 * Validates: Requirements 1.2, 1.3, 1.6
 * - Requirement 1.2: THE Console SHALL use modern UI patterns including cards
 *   with subtle shadows, rounded corners, and smooth transitions.
 * - Requirement 1.3: WHEN a user interacts with interactive elements, THE Console
 *   SHALL provide visual feedback within 100ms.
 * - Requirement 1.6: THE Console SHALL use accessible color contrasts meeting
 *   WCAG 2.1 AA standards for all text elements.
 */

import React, { forwardRef, useId, useState } from 'react';
import { tokens } from '../tokens';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text displayed above the input */
  label?: string;
  /** Error message to display below the input */
  error?: string;
  /** Helper text displayed below the input (when no error) */
  helperText?: string;
  /** Size variant of the input */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the input takes full width */
  fullWidth?: boolean;
  /** Icon to display at the start of the input */
  startIcon?: React.ReactNode;
  /** Icon to display at the end of the input */
  endIcon?: React.ReactNode;
}

/**
 * Get size-specific styles
 */
function getSizeStyles(size: 'sm' | 'md' | 'lg'): {
  input: React.CSSProperties;
  label: React.CSSProperties;
} {
  switch (size) {
    case 'sm':
      return {
        input: {
          padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
          fontSize: tokens.typography.fontSize.sm,
          minHeight: '32px',
        },
        label: {
          fontSize: tokens.typography.fontSize.xs,
          marginBottom: tokens.spacing[1],
        },
      };
    case 'lg':
      return {
        input: {
          padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
          fontSize: tokens.typography.fontSize.lg,
          minHeight: '48px',
        },
        label: {
          fontSize: tokens.typography.fontSize.base,
          marginBottom: tokens.spacing[2],
        },
      };
    case 'md':
    default:
      return {
        input: {
          padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
          fontSize: tokens.typography.fontSize.base,
          minHeight: '40px',
        },
        label: {
          fontSize: tokens.typography.fontSize.sm,
          marginBottom: tokens.spacing[1],
        },
      };
  }
}

/**
 * Input component with label, error, and helper text support.
 *
 * Color contrast ratios meet WCAG 2.1 AA standards:
 * - Label text: neutral-700 on light background (contrast ratio > 4.5:1)
 * - Input text: neutral-900 on neutral-50 background (contrast ratio > 7:1)
 * - Placeholder: neutral-400 (decorative, not required for AA)
 * - Error text: error-dark on light background (contrast ratio > 4.5:1)
 * - Helper text: neutral-500 on light background (contrast ratio > 4.5:1)
 *
 * @example
 * ```tsx
 * // Basic input with label
 * <Input label="Email" type="email" placeholder="Enter your email" />
 *
 * // Input with error state
 * <Input
 *   label="Password"
 *   type="password"
 *   error="Password must be at least 8 characters"
 * />
 *
 * // Disabled input
 * <Input label="Username" value="john_doe" disabled />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    error,
    helperText,
    size = 'md',
    fullWidth = false,
    disabled = false,
    startIcon,
    endIcon,
    style,
    id: providedId,
    onFocus,
    onBlur,
    ...props
  },
  ref
) {
  const generatedId = useId();
  const inputId = providedId || generatedId;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;

  const [isFocused, setIsFocused] = useState(false);

  const sizeStyles = getSizeStyles(size);
  const hasError = Boolean(error);

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: fullWidth ? '100%' : 'auto',
    ...style,
  };

  const labelStyles: React.CSSProperties = {
    fontFamily: tokens.typography.fontFamily.sans,
    fontWeight: tokens.typography.fontWeight.medium,
    color: hasError ? tokens.colors.error.dark : tokens.colors.neutral[700],
    ...sizeStyles.label,
  };

  const inputWrapperStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const inputStyles: React.CSSProperties = {
    width: '100%',
    fontFamily: tokens.typography.fontFamily.sans,
    color: tokens.colors.neutral[900],
    backgroundColor: disabled ? tokens.colors.neutral[100] : tokens.colors.neutral[50],
    border: `1px solid ${
      hasError
        ? tokens.colors.error.main
        : isFocused
        ? tokens.colors.accent[500]
        : tokens.colors.neutral[300]
    }`,
    borderRadius: tokens.borderRadius.md,
    boxShadow:
      isFocused && !hasError
        ? `0 0 0 3px ${tokens.semantic.accentRing}`
        : undefined,
    // Transition for visual feedback within 100ms (using 150ms fast token)
    transition: `all ${tokens.transitions.fast}`,
    outline: 'none',
    cursor: disabled ? 'not-allowed' : 'text',
    opacity: disabled ? 0.7 : 1,
    paddingLeft: startIcon ? tokens.spacing[10] : undefined,
    paddingRight: endIcon ? tokens.spacing[10] : undefined,
    ...sizeStyles.input,
  };

  const iconStyles: React.CSSProperties = {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colors.neutral[400],
    pointerEvents: 'none',
  };

  const startIconStyles: React.CSSProperties = {
    ...iconStyles,
    left: tokens.spacing[3],
  };

  const endIconStyles: React.CSSProperties = {
    ...iconStyles,
    right: tokens.spacing[3],
  };

  const messageStyles: React.CSSProperties = {
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: tokens.typography.fontSize.sm,
    marginTop: tokens.spacing[1],
    lineHeight: tokens.typography.lineHeight.normal,
  };

  const errorStyles: React.CSSProperties = {
    ...messageStyles,
    color: tokens.colors.error.dark,
  };

  const helperStyles: React.CSSProperties = {
    ...messageStyles,
    color: tokens.colors.neutral[500],
  };

  return (
    <div style={containerStyles}>
      {label && (
        <label htmlFor={inputId} style={labelStyles}>
          {label}
        </label>
      )}
      <div style={inputWrapperStyles}>
        {startIcon && <span style={startIconStyles}>{startIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? errorId : helperText ? helperId : undefined
          }
          style={inputStyles}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          {...props}
        />
        {endIcon && <span style={endIconStyles}>{endIcon}</span>}
      </div>
      {hasError && (
        <span id={errorId} role="alert" style={errorStyles}>
          {error}
        </span>
      )}
      {!hasError && helperText && (
        <span id={helperId} style={helperStyles}>
          {helperText}
        </span>
      )}
    </div>
  );
});

export default Input;
